import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash/debounce';
import uniqueId from 'lodash/uniqueId';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';

import { getHistoryState } from '@bibliocommons/utils-browser';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { childrenShape } from '@bibliocommons/bc-prop-types';
import { IconButton } from '@bibliocommons/base-buttons';
import { Close } from '@bibliocommons/base-icons';
import Context from '@bibliocommons/context';

import Portal from 'app/components/shared/Portal';
import ModalHeadingContext from './ModalHeadingContext';
import './Modal.scss';

const ESCAPE_KEY_NAME = 'Escape';
const ESCAPE_KEY_CODE = 27;

function close() {
  window.history.back();
}

function Modal({
  open = false,
  className,
  bodyClassName,
  children,
  dataKey,
  size = 'default',
  skipHistoryEntry = false,
  handleClose
}) {
  const context = useContext(Context);

  const modalBodyRef = useRef(null);
  const modalIdRef = useRef(null);
  if (modalIdRef.current === null) {
    modalIdRef.current = uniqueId('modalId');
  }

  // The dialog's accessible name. Before CORE-78924 this element had no aria-label,
  // aria-labelledby or aria-describedby at all, so every modal in core computed to name ""
  // and description "" -- assistive tech announced "dialog" and nothing more, even though the
  // heading was rendered on screen. ModalHeader registers itself here so the name comes from
  // the heading the user can actually see.
  const headingId = `${modalIdRef.current}-heading`;
  const [hasHeading, setHasHeading] = useState(false);
  const headingContext = useMemo(() => ({ headingId, registerHeading: () => setHasHeading(true) }), [headingId]);

  // Keep the latest prop values available to stable event handlers without
  // changing their identity (so addEventListener/removeEventListener pair up).
  const openRef = useRef(open);
  const handleCloseRef = useRef(handleClose);
  const skipHistoryEntryRef = useRef(skipHistoryEntry);
  openRef.current = open;
  handleCloseRef.current = handleClose;
  skipHistoryEntryRef.current = skipHistoryEntry;

  // Escape and outside-click normally close by pressing Back, since the modal owns a history entry.
  // With skipHistoryEntry there is no entry to pop, so they close the modal directly.
  const dismiss = useCallback(() => {
    if (skipHistoryEntryRef.current) {
      handleCloseRef.current();
      return;
    }

    close();
  }, []);

  const closeOnEscape = useCallback(
    e => {
      if (e.key === ESCAPE_KEY_NAME || e.which === ESCAPE_KEY_CODE) {
        dismiss();
      }
    },
    [dismiss]
  );

  const adjustModalHeight = useCallback(() => {
    // We must adjust the modal body's height because some
    // mobile browsers cover the bottom with the browser chrome.

    if (modalBodyRef.current && window) {
      const screenXsMaxWidth = 575; // $screen-xs-max CSS variable value

      if (window.innerWidth <= screenXsMaxWidth) {
        const maxHeightPx = `${window.innerHeight}px`;

        modalBodyRef.current.style.maxHeight = maxHeightPx;
        modalBodyRef.current.style.minHeight = maxHeightPx;
      } else {
        modalBodyRef.current.style.maxHeight = null;
        modalBodyRef.current.style.minHeight = null;
      }
    }
  }, []);

  const debouncedAdjustModalHeight = useMemo(() => debounce(adjustModalHeight, 250), [adjustModalHeight]);

  const closeOnPopState = useCallback(() => {
    const historyState = getHistoryState();

    if (openRef.current && historyState.modalId !== modalIdRef.current) {
      handleCloseRef.current();
    }
  }, []);

  const closeOnDocumentClick = useCallback(
    e => {
      // If clicking outside the modal's content, call the close handler
      const contentIsTarget = modalBodyRef.current && modalBodyRef.current.contains(e.target);
      if (document.body.contains(e.target) && !contentIsTarget) {
        dismiss();
      }
    },
    [dismiss]
  );

  const handleToggle = useCallback(
    isOpen => {
      // Add the event listener on open, and remove on close
      const handleEventListener = isOpen ? 'addEventListener' : 'removeEventListener';
      document[handleEventListener]('click', closeOnDocumentClick, true);
      document[handleEventListener]('keydown', closeOnEscape, true);
      window[handleEventListener]('resize', debouncedAdjustModalHeight);

      // Without an entry of its own there is nothing for a popstate to mean: the navigation that
      // fired it is someone else's, and closing on it would dismiss the modal during, for example,
      // the router's block-and-restore of a guarded Back press (CORE-78999).
      if (!skipHistoryEntryRef.current) {
        window[handleEventListener]('popstate', closeOnPopState);
      }

      const handleClass = isOpen ? 'add' : 'remove';
      document.documentElement.classList[handleClass]('modal-is-open');

      const historyState = getHistoryState();

      if (isOpen && skipHistoryEntryRef.current) {
        debouncedAdjustModalHeight();
      } else if (isOpen) {
        // Adding an entry is what makes Back close the modal. The catch: the copied state
        // includes `idx`, history@5's count of which entry you are on, so this entry is labelled
        // the same as the one below it and the router -- which never sees a raw pushState --
        // still thinks it is on that one. Code that measures how far a Back press moved is then
        // off by one, which is what breaks the unsaved-changes prompt in admin/browse. Proper
        // fix: add the entry through the router, so it gets its own number. Not done here, since
        // it changes Back for every modal in core.
        // https://bibliocommons.atlassian.net/browse/CORE-79027
        window.history.pushState({ ...historyState, modalId: modalIdRef.current }, '');
        debouncedAdjustModalHeight();
      } else if (!skipHistoryEntryRef.current && historyState.modalId === modalIdRef.current) {
        close();
      }
    },
    [closeOnDocumentClick, closeOnEscape, closeOnPopState, debouncedAdjustModalHeight]
  );

  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      if (open) {
        handleToggle(open);
      }
    } else {
      handleToggle(open);
    }
  }, [open, handleToggle]);

  useEffect(
    () => () => {
      handleToggle(false);
    },
    [handleToggle]
  );

  const classNames = cn('cp-modal', className);
  const bodyClassNames = cn('modal__body', bodyClassName, {
    [`modal__body--${size}`]: !!size
  });

  return (
    <Portal focusTrapActive={open}>
      <CSSTransition in={open} classNames="modal-fade" timeout={{ enter: 150, exit: 75 }} mountOnEnter unmountOnExit>
        <div
          className={classNames}
          role="dialog"
          aria-hidden={!open}
          aria-labelledby={hasHeading ? headingId : undefined}
          data-key={dataKey}
        >
          <div className={bodyClassNames} ref={modalBodyRef}>
            <div>
              <ModalHeadingContext.Provider value={headingContext}>{children}</ModalHeadingContext.Provider>
            </div>
            {!context.mobileApp && (
              <IconButton dataKey="modal-close" className="modal__body__close" handleClick={dismiss}>
                <Close />
                <ScreenReaderMessage>
                  <FormattedMessage id="close_modal" />
                </ScreenReaderMessage>
              </IconButton>
            )}
          </div>
        </div>
      </CSSTransition>
    </Portal>
  );
}

Modal.propTypes = {
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  children: childrenShape.isRequired,
  dataKey: PropTypes.string,
  open: PropTypes.bool,
  size: PropTypes.oneOf(['default', 'small', 'large']),
  // Opt out of the history entry the modal normally adds so Back can close it. For a dialog the
  // router itself put on screen -- the unsaved-changes guard -- Back is already spoken for, and an
  // entry pushed mid-block corrupts the router's restore (CORE-78999, and see CORE-79027 for the
  // wider problem with this component writing to history directly).
  skipHistoryEntry: PropTypes.bool
};

export default Modal;
